/**
 * SenderKeyMessage — the per-message wire form of a group ciphertext.
 *
 * Each message carries: the (groupId, senderId, deviceId) routing
 * triple, the chainIndex it was sealed at, the AES-GCM ciphertext, and
 * an Ed25519 signature over the routing fields and ciphertext using
 * the sender's per-group signing key.
 *
 * ## Receiver flow
 *   1. Verify the signature against the SenderKey's signingPublicKey
 *      BEFORE attempting AES-GCM open. A tampered ciphertext would
 *      otherwise trigger a spurious GCM auth failure that is
 *      indistinguishable (to the caller) from a genuine ratchet
 *      desync — and would waste compute on doomed work.
 *   2. Fast-forward the local chain key from `state.chainIndex` to
 *      `message.chainIndex`, capped at MAX_LOOKAHEAD so a hostile
 *      message claiming chainIndex = 2^31 cannot DoS us.
 *   3. Derive the message key, AES-GCM open with all-zeros nonce
 *      (each message key is fresh, so reuse is impossible).
 *   4. Update local state to chainIndex + 1.
 *
 * ## Wire format
 *
 *   kaether:skmsg:v1:<base64url(JSON)>
 *
 * ## iOS parity
 * Mirrors `kaether_for_ios/Stoat/E2EE/SenderKeyMessage.swift`
 * byte-for-byte. Prefix: `kaether:skmsg:v1:`.
 *
 * Pure logic + SubtleCrypto only. No SolidJS imports.
 */

import {
  validateChainKeyLength,
  advanceChainKey,
  deriveMessageKey,
  wrapEd25519SeedAsPkcs8,
  MAX_LOOKAHEAD,
  type SenderKeyState,
} from "./SenderKey";
import { SenderKeyError } from "./SenderKey";

// ─── types ────────────────────────────────────────────────────────────────────

export interface SenderKeyMessage {
  readonly schema: number;
  readonly groupId: string;
  readonly senderId: string;
  readonly deviceId: number;
  readonly chainIndex: number;
  /** AES-GCM combined output (nonce ‖ ciphertext ‖ tag), base64-encoded. */
  readonly ciphertext: string;
  /** Ed25519 signature over canonical payload bytes, base64-encoded. */
  readonly signature: string;
}

const CURRENT_SCHEMA = 1;

// ─── base64url helpers ────────────────────────────────────────────────────────

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function fromBase64Url(str: string): Uint8Array {
  let s = str.replace(/-/g, "+").replace(/_/g, "/");
  while (s.length % 4 !== 0) s += "=";
  const binary = atob(s);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function fromBase64(str: string): Uint8Array {
  let s = str;
  while (s.length % 4 !== 0) s += "=";
  const binary = atob(s);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// ─── canonical bytes ──────────────────────────────────────────────────────────

const enc = new TextEncoder();

function writeI64BE(view: DataView, offset: number, value: number): void {
  const high = Math.floor(value / 0x1_0000_0000);
  const low = value >>> 0;
  view.setUint32(offset, high >>> 0, false);
  view.setUint32(offset + 4, low, false);
}

/**
 * Canonical bytes the Ed25519 signature covers. Includes every routing
 * field plus the ciphertext, so a server cannot rebind a ciphertext to
 * a different (group, sender, device, index) tuple.
 *
 *   schema(8BE) NUL groupId(utf8) NUL senderId(utf8) NUL
 *   deviceId(8BE) NUL chainIndex(8BE) NUL ciphertext(raw)
 *
 * Matches iOS SenderKeyMessage.signaturePayload byte-for-byte.
 */
function signaturePayload(
  schema: number,
  groupId: string,
  senderId: string,
  deviceId: number,
  chainIndex: number,
  ciphertextBytes: Uint8Array,
): Uint8Array {
  const groupIdBytes = enc.encode(groupId);
  const senderIdBytes = enc.encode(senderId);

  const totalLen =
    8 + 1 +
    groupIdBytes.byteLength + 1 +
    senderIdBytes.byteLength + 1 +
    8 + 1 +
    8 + 1 +
    ciphertextBytes.byteLength;

  const buf = new Uint8Array(totalLen);
  const view = new DataView(buf.buffer);
  let pos = 0;

  writeI64BE(view, pos, schema);
  pos += 8;
  buf[pos++] = 0x00;

  buf.set(groupIdBytes, pos);
  pos += groupIdBytes.byteLength;
  buf[pos++] = 0x00;

  buf.set(senderIdBytes, pos);
  pos += senderIdBytes.byteLength;
  buf[pos++] = 0x00;

  writeI64BE(view, pos, deviceId);
  pos += 8;
  buf[pos++] = 0x00;

  writeI64BE(view, pos, chainIndex);
  pos += 8;
  buf[pos++] = 0x00;

  buf.set(ciphertextBytes, pos);

  return buf;
}

// ─── codec ────────────────────────────────────────────────────────────────────

export const SKMSG_PREFIX = "kaether:skmsg:v1:";

export function isSKMessage(s: string): boolean {
  return s.startsWith(SKMSG_PREFIX);
}

export function encodeSKMessage(msg: SenderKeyMessage): string {
  const json = JSON.stringify(msg);
  const jsonBytes = enc.encode(json);
  return SKMSG_PREFIX + toBase64Url(jsonBytes);
}

export function decodeSKMessage(s: string): SenderKeyMessage {
  if (!s.startsWith(SKMSG_PREFIX)) {
    throw new SenderKeyError("malformedMessage", "not a SenderKeyMessage");
  }
  const b64 = s.slice(SKMSG_PREFIX.length);
  let jsonBytes: Uint8Array;
  try {
    jsonBytes = fromBase64Url(b64);
  } catch {
    throw new SenderKeyError("malformedMessage", "invalid base64url");
  }

  let msg: SenderKeyMessage;
  try {
    const text = new TextDecoder().decode(jsonBytes);
    msg = JSON.parse(text) as SenderKeyMessage;
  } catch {
    throw new SenderKeyError("malformedMessage", "JSON parse failed");
  }

  if (msg.schema !== CURRENT_SCHEMA) {
    throw new SenderKeyError("unsupportedSchema", `schema ${msg.schema}`);
  }
  if (!msg.groupId) {
    throw new SenderKeyError("malformedMessage", "empty groupId");
  }
  if (!msg.senderId) {
    throw new SenderKeyError("malformedMessage", "empty senderId");
  }
  if (msg.chainIndex < 0) {
    throw new SenderKeyError("malformedMessage", "negative chainIndex");
  }

  return msg;
}

// ─── seal / open ──────────────────────────────────────────────────────────────

/**
 * Seals `plaintext` under the sender's current chain state.
 *
 * The returned `newState` has its chainKey advanced and chainIndex
 * incremented by 1. The caller must persist `newState` before shipping
 * the message — otherwise a crash mid-send could cause the next send to
 * reuse a chain index, which receivers reject as stale.
 */
export async function sealSKMessage(
  plaintext: Uint8Array,
  state: SenderKeyState,
  groupId: string,
  senderId: string,
  deviceId: number,
): Promise<{ message: SenderKeyMessage; newState: SenderKeyState }> {
  if (state.signingPrivateKeyBytes === null) {
    throw new SenderKeyError("malformedMessage", "seal requires a signing private key");
  }
  validateChainKeyLength(state.chainKeyBytes);

  const msgKeyBytes = await deriveMessageKey(state.chainKeyBytes, state.chainIndex);

  // AES-GCM key from the raw message key bytes
  const aesKey = await crypto.subtle.importKey(
    "raw",
    msgKeyBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt"],
  );

  // All-zeros nonce is safe: each messageKey is unique because it derives
  // from a unique (chainKey, index) pair, and the chain advances after
  // every seal.
  const nonce = new Uint8Array(12); // 12 all-zero bytes
  let ciphertextBuf: ArrayBuffer;
  try {
    ciphertextBuf = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce },
      aesKey,
      plaintext,
    );
  } catch {
    throw new SenderKeyError("decryptionFailed", "AES-GCM encrypt failed");
  }

  // Combined = nonce ‖ ciphertext (nonce prepended so receiver can extract it)
  const ctBytes = new Uint8Array(ciphertextBuf);
  const combined = new Uint8Array(nonce.length + ctBytes.length);
  combined.set(nonce, 0);
  combined.set(ctBytes, nonce.length);

  const payload = signaturePayload(
    CURRENT_SCHEMA,
    groupId,
    senderId,
    deviceId,
    state.chainIndex,
    combined,
  );

  let privKey: CryptoKey;
  try {
    // Chrome rejects raw private-key import for Ed25519 — wrap in PKCS#8.
    privKey = await crypto.subtle.importKey(
      "pkcs8",
      wrapEd25519SeedAsPkcs8(state.signingPrivateKeyBytes),
      { name: "Ed25519" },
      false,
      ["sign"],
    );
  } catch {
    throw new SenderKeyError("malformedMessage", "failed to import signing key");
  }

  const sigBuf = await crypto.subtle.sign({ name: "Ed25519" }, privKey, payload);
  const signatureBytes = new Uint8Array(sigBuf);

  const message: SenderKeyMessage = {
    schema: CURRENT_SCHEMA,
    groupId,
    senderId,
    deviceId,
    chainIndex: state.chainIndex,
    ciphertext: toBase64(combined),
    signature: toBase64(signatureBytes),
  };

  const nextChainKeyBytes = await advanceChainKey(state.chainKeyBytes);
  const newState: SenderKeyState = {
    chainKeyBytes: nextChainKeyBytes,
    chainIndex: state.chainIndex + 1,
    signingPublicKeyBytes: state.signingPublicKeyBytes,
    signingPrivateKeyBytes: state.signingPrivateKeyBytes,
  };

  return { message, newState };
}

/**
 * Opens a message and returns the resulting plaintext + the new
 * receiver-side state.
 *
 * Verifies the Ed25519 signature BEFORE attempting AES-GCM. Out-of-order
 * delivery (`message.chainIndex < state.chainIndex`) is rejected because
 * the chain ratchet is one-way. Forward jumps are permitted up to MAX_LOOKAHEAD.
 */
export async function openSKMessage(
  message: SenderKeyMessage,
  state: SenderKeyState,
): Promise<{ plaintext: Uint8Array; newState: SenderKeyState }> {
  validateChainKeyLength(state.chainKeyBytes);

  if (message.chainIndex < state.chainIndex) {
    throw new SenderKeyError("malformedMessage", "stale chainIndex");
  }

  const jump = message.chainIndex - state.chainIndex;
  if (jump > MAX_LOOKAHEAD) {
    throw new SenderKeyError(
      "lookaheadLimitExceeded",
      `jump of ${jump} exceeds MAX_LOOKAHEAD=${MAX_LOOKAHEAD}`,
    );
  }

  const combinedBytes = fromBase64(message.ciphertext);
  const signatureBytes = fromBase64(message.signature);

  // Signature first — a tampered ciphertext otherwise triggers a GCM auth
  // failure that wastes compute and is harder to distinguish from a genuine
  // ratchet desync.
  const payload = signaturePayload(
    message.schema,
    message.groupId,
    message.senderId,
    message.deviceId,
    message.chainIndex,
    combinedBytes,
  );

  let pubKey: CryptoKey;
  try {
    pubKey = await crypto.subtle.importKey(
      "raw",
      state.signingPublicKeyBytes,
      { name: "Ed25519" },
      false,
      ["verify"],
    );
  } catch {
    throw new SenderKeyError("signatureVerificationFailed", "failed to import public key");
  }

  let valid: boolean;
  try {
    valid = await crypto.subtle.verify(
      { name: "Ed25519" },
      pubKey,
      signatureBytes,
      payload,
    );
  } catch {
    throw new SenderKeyError("signatureVerificationFailed", "verify threw");
  }

  if (!valid) {
    throw new SenderKeyError("signatureVerificationFailed", "signature did not verify");
  }

  // Fast-forward the chain to message.chainIndex
  let chainKeyBytes = state.chainKeyBytes;
  let index = state.chainIndex;
  while (index < message.chainIndex) {
    chainKeyBytes = await advanceChainKey(chainKeyBytes);
    index++;
  }

  // Derive message key at the target index
  const msgKeyBytes = await deriveMessageKey(chainKeyBytes, message.chainIndex);
  const aesKey = await crypto.subtle.importKey(
    "raw",
    msgKeyBytes,
    { name: "AES-GCM" },
    false,
    ["decrypt"],
  );

  // Extract nonce (first 12 bytes) and actual ciphertext (rest)
  if (combinedBytes.length < 12) {
    throw new SenderKeyError("decryptionFailed", "ciphertext too short");
  }
  const nonce = combinedBytes.slice(0, 12);
  const ct = combinedBytes.slice(12);

  let plaintextBuf: ArrayBuffer;
  try {
    plaintextBuf = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonce },
      aesKey,
      ct,
    );
  } catch {
    throw new SenderKeyError("decryptionFailed", "AES-GCM decrypt failed");
  }

  const plaintext = new Uint8Array(plaintextBuf);

  const nextChainKeyBytes = await advanceChainKey(chainKeyBytes);
  const newState: SenderKeyState = {
    chainKeyBytes: nextChainKeyBytes,
    chainIndex: message.chainIndex + 1,
    signingPublicKeyBytes: state.signingPublicKeyBytes,
    signingPrivateKeyBytes: state.signingPrivateKeyBytes,
  };

  return { plaintext, newState };
}
